import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import logoBlanco from '../assets/marca/kodica-white.svg';
import { EscanerQr } from '../componentes/EscanerQr';
import { Iconos } from '../componentes/iconos';
import { Boton, Campo, Entrada, ErrorCaja } from '../componentes/ui';
import { almacenToken } from '../lib/api';
import {
  anclar,
  enGrupos,
  EtiquetaInvalida,
  huellaAnclada,
  leerEtiqueta,
  mirarCertificado,
  olvidarAncla,
  requiereEmparejar,
  type Certificado,
} from '../lib/emparejamiento';
import {
  DireccionInvalida,
  esAppNativa,
  NoSeConecto,
  guardarServidor,
  normalizarServidor,
  probarServidor,
  servidorGuardado,
} from '../lib/servidor';

/**
 * Dónde está el servidor del condominio. Solo existe en el APK: en el
 * navegador la web y el API comparten origen y no hay nada que preguntar.
 *
 * Se muestra antes del login la primera vez, porque sin dirección el propio
 * login no tiene a quién preguntarle.
 *
 * ## El emparejamiento
 *
 * Con una dirección `https://` hay un paso más, y es el que sostiene la
 * seguridad de toda la instalación: hay que saber que el certificado que
 * responde es el de esta caseta y no el de quien se hace pasar por ella.
 *
 * Se puede de dos formas, y **la buena es escanear**. La etiqueta pegada al
 * equipo trae la huella en un QR; leyéndola, la comparación la hace la máquina
 * y es exacta. Comparar sesenta y cuatro caracteres a ojo es el eslabón débil
 * de todo esto —se miran los primeros cuatro y se da por bueno el resto—, así
 * que esa vía se queda para cuando no hay cámara o la etiqueta se perdió.
 *
 * Ver `lib/emparejamiento` y `infra/certificado/etiqueta.mjs`.
 */

/** En qué punto del emparejamiento vamos. */
type Paso =
  | { fase: 'direccion' }
  /** La cámara abierta, buscando la etiqueta del equipo. */
  | { fase: 'escanear' }
  /** Sin etiqueta: toca comparar la huella a mano. */
  | { fase: 'comparar'; url: string; cert: Certificado }
  /**
   * Lo que responde esa dirección no es lo que se esperaba. `origen` dice
   * contra qué se esperaba, que cambia por completo lo que significa.
   */
  | {
      fase: 'noCoincide';
      url: string;
      cert: Certificado;
      esperada: string;
      origen: 'anclada' | 'etiqueta';
    };

export function PantallaServidor({
  alGuardar,
  alCancelar,
}: {
  alGuardar: () => void;
  alCancelar?: () => void;
}) {
  const [direccion, setDireccion] = useState(servidorGuardado() ?? '');
  const [paso, setPaso] = useState<Paso>({ fase: 'direccion' });
  const [error, setError] = useState<string | null>(null);
  const [probando, setProbando] = useState(false);

  /**
   * La huella que traía la etiqueta escaneada, si se escaneó una.
   *
   * Se guarda en vez de usarse y tirarse porque una etiqueta puede no traer la
   * dirección —es opcional, ver `etiqueta.mjs`—. Cuando falta, se escanea, se
   * teclea la dirección y se pulsa Conectar: la huella leída sigue aquí, así
   * que no hay que volver a apuntar la cámara.
   */
  const [deEtiqueta, setDeEtiqueta] = useState<string | null>(null);

  /** Lo último: comprobar que del otro lado hay un Kodica y quedárselo. */
  async function conectar(url: string) {
    await probarServidor(url);

    // El token lo firmó el servidor anterior; en otro no vale. Arrastrarlo
    // solo consigue un 401 en la primera pantalla, que se lee como si la
    // dirección nueva estuviera mal.
    if (servidorGuardado() !== url) almacenToken.borrar();

    guardarServidor(url);
    alGuardar();
  }

  /**
   * El camino común de las dos vías.
   *
   * Toma la dirección y la huella por argumento en vez de leerlas del estado:
   * al escanear se sabe la dirección en ese instante, y `setDireccion` todavía
   * no habría surtido efecto cuando esto corriera.
   */
  async function intentar(texto: string, esperadaDeEtiqueta: string | null) {
    setError(null);
    setProbando(true);
    try {
      const url = normalizarServidor(texto);
      const puedeEmparejar = requiereEmparejar(url);

      // ── ¿Ya hay una huella que respetar? ────────────────────────────
      //
      // Si esta dirección se emparejó antes, o si se acaba de escanear su
      // etiqueta, se comprueba **siempre** y antes que nada. Sale más barato
      // que descubrir la suplantación por un fallo de red: sin esto, una huella
      // cambiada se vería como «no se pudo conectar» y quien lo lea va a
      // revisar el cable, no la seguridad.
      const anclada = puedeEmparejar ? await huellaAnclada(url) : null;
      const esperada = esperadaDeEtiqueta ?? anclada;

      if (puedeEmparejar && esperada) {
        const cert = await mirarCertificado(url);
        if (esperada !== cert.huella) {
          setPaso({
            fase: 'noCoincide',
            url,
            cert,
            esperada,
            origen: esperadaDeEtiqueta ? 'etiqueta' : 'anclada',
          });
          return;
        }
        // Coincide con la etiqueta: se ancla y se sigue sin preguntar nada.
        // Preguntar aquí sería pedirle a alguien que confirme una comparación
        // que la máquina acaba de hacer mejor que él.
        if (esperadaDeEtiqueta && !anclada) await anclar(url, cert.huella);
        await conectar(url);
        return;
      }

      // ── Sin ancla: primero se intenta como cualquier dirección ──────
      //
      // **No todo `https://` necesita emparejarse, y darlo por hecho rompía
      // cosas.** Un servidor con dominio de verdad —el día que lo haya, o
      // detrás de un túnel de Cloudflare para enseñárselo a alguien— trae un
      // certificado que el sistema ya avala. Ahí no hay huella que comparar ni
      // etiqueta que exista, y peor: anclarlo dejaría a la tablet fuera el día
      // que ese certificado se renueve, cosa que hacen solos.
      //
      // Se prueba la conexión normal, y que conteste **es** la respuesta: si el
      // sistema aceptó el certificado, no hay nada que emparejar.
      try {
        await conectar(url);
        return;
      } catch (e) {
        // Que responda algo que no es Kodica es una dirección mal escrita, no
        // un problema de confianza: no lleva a emparejar.
        if (!puedeEmparejar || !(e instanceof NoSeConecto)) throw e;
      }

      // ── No se pudo. ¿Hay un servidor ahí que el sistema no acepta? ──
      //
      // Si se puede leer su certificado, sí lo hay, y lo que falló fue el aval
      // — que es exactamente lo que el emparejamiento resuelve. Si tampoco se
      // puede, es un problema de red y `mirarCertificado` lo dice mejor que un
      // mensaje genérico.
      const cert = await mirarCertificado(url);
      setPaso({ fase: 'comparar', url, cert });
    } catch (e) {
      setError(
        e instanceof DireccionInvalida || e instanceof EtiquetaInvalida
          ? e.message
          : e instanceof Error && e.message
            ? e.message
            : 'No se pudo probar la dirección.',
      );
      setPaso({ fase: 'direccion' });
    } finally {
      setProbando(false);
    }
  }

  function alLeerCodigo(texto: string) {
    let etiqueta;
    try {
      etiqueta = leerEtiqueta(texto);
    } catch (e) {
      // Se sigue con la cámara abierta: lo normal es que haya pasado por
      // delante otro código, y cerrar la pantalla obligaría a empezar de cero.
      setError(e instanceof EtiquetaInvalida ? e.message : 'Ese código no se entiende.');
      return;
    }

    // Puede no traer huella: un código de solo dirección, para un servidor cuyo
    // certificado ya avala una autoridad. Entonces no hay nada que anclar y el
    // camino normal se encarga.
    setDeEtiqueta(etiqueta.huella ?? null);
    setPaso({ fase: 'direccion' });

    const destino = etiqueta.servidor ?? direccion;
    if (!destino) {
      setError('La etiqueta no trae la dirección. Escríbela y pulsa Conectar.');
      return;
    }
    if (etiqueta.servidor) setDireccion(etiqueta.servidor);
    void intentar(destino, etiqueta.huella ?? null);
  }

  /** La persona comparó la huella a mano y dice que sí. */
  async function aceptar(url: string, huella: string) {
    setError(null);
    setProbando(true);
    try {
      await anclar(url, huella);
      await conectar(url);
    } catch (e) {
      // El ancla ya quedó guardada; si falla la prueba es otra cosa —el API
      // apagado, por ejemplo— y reintentar desde la dirección es lo correcto.
      setPaso({ fase: 'direccion' });
      setError(e instanceof DireccionInvalida ? e.message : 'No se pudo conectar al servidor.');
    } finally {
      setProbando(false);
    }
  }

  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-stack-lg bg-on-primary-container p-gutter">
      <img src={logoBlanco} alt="Kodica" className="h-11 w-auto" />

      <div className="w-full max-w-sm rounded-xl border border-outline-variant bg-surface-container-lowest p-8">
        {paso.fase === 'escanear' ? (
          <>
            <EscanerQr
              titulo="Escanea la etiqueta"
              ayuda="Está pegada al equipo de la caseta, con un código cuadrado."
              onLeido={alLeerCodigo}
              onCerrar={() => {
                setError(null);
                setPaso({ fase: 'direccion' });
              }}
            />
            {error && <ErrorCaja>{error}</ErrorCaja>}
          </>
        ) : paso.fase === 'comparar' ? (
          <Comparar
            cert={paso.cert}
            ocupado={probando}
            alAceptar={() => aceptar(paso.url, paso.cert.huella)}
            alRechazar={() => setPaso({ fase: 'direccion' })}
          />
        ) : paso.fase === 'noCoincide' ? (
          <NoCoincide
            cert={paso.cert}
            esperada={paso.esperada}
            origen={paso.origen}
            alVolver={() => {
              setDeEtiqueta(null);
              setPaso({ fase: 'direccion' });
            }}
            alReemparejar={async () => {
              await olvidarAncla(paso.url);
              setDeEtiqueta(null);
              setPaso({ fase: 'comparar', url: paso.url, cert: paso.cert });
            }}
          />
        ) : (
          <>
            <header className="mb-stack-lg">
              <h1 className="text-headline-md text-on-surface">Servidor del condominio</h1>
              <p className="mt-1 text-body-md text-on-surface-variant">
                La dirección del equipo de la caseta. Te la da quien instaló el sistema.
              </p>
            </header>

            {/* Escanear va primero y como botón principal: es la vía buena, y
                la que además ahorra teclear una IP en una pantalla táctil. */}
            {esAppNativa() && (
              <Boton
                variante="primario"
                className="mb-5 w-full"
                icono={Iconos.credencial}
                disabled={probando}
                onClick={() => {
                  setError(null);
                  setPaso({ fase: 'escanear' });
                }}
              >
                Escanear etiqueta del equipo
              </Boton>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                void intentar(direccion, deEtiqueta);
              }}
              className="space-y-5"
            >
              <Campo
                etiqueta="Dirección"
                ayuda={
                  deEtiqueta
                    ? 'La etiqueta ya se leyó. Escribe la dirección y conecta.'
                    : 'Si no escribes http:// o https://, se asume por el puerto.'
                }
              >
                <Entrada
                  id="direccion"
                  // Igual que en el login: el corrector del teléfono convierte una
                  // IP en cualquier cosa y el error resultante no se entiende.
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="off"
                  spellCheck={false}
                  inputMode="url"
                  placeholder="192.168.68.119:8443"
                  required
                  value={direccion}
                  onChange={(e) => setDireccion(e.target.value)}
                />
              </Campo>

              {error && <ErrorCaja>{error}</ErrorCaja>}

              <Boton
                type="submit"
                variante={esAppNativa() ? 'contorno' : 'primario'}
                disabled={probando}
                icono={Iconos.aparatos}
                className="w-full"
              >
                {probando ? 'Probando…' : 'Conectar'}
              </Boton>

              {alCancelar && (
                <Boton type="button" variante="texto" className="w-full" onClick={alCancelar}>
                  Cancelar
                </Boton>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * La huella, para leerla en voz alta o comparar con la etiqueta.
 *
 * En bloques y en monoespaciada a propósito. Una cadena de sesenta y cuatro
 * caracteres seguidos no se compara: se miran los primeros cuatro y se da por
 * bueno el resto.
 */
function Huella({ valor, tono = 'normal' }: { valor: string; tono?: 'normal' | 'malo' }) {
  return (
    <div
      className={`rounded-lg border p-3 font-mono text-body-sm leading-relaxed tracking-wide ${
        tono === 'malo'
          ? 'border-error-container bg-error-container text-on-error-container'
          : 'border-outline-variant bg-surface-container text-on-surface'
      }`}
    >
      {enGrupos(valor).map((fila) => (
        <div key={fila}>{fila}</div>
      ))}
    </div>
  );
}

/** El emparejamiento a mano, para cuando no hay etiqueta ni cámara. */
function Comparar({
  cert,
  ocupado,
  alAceptar,
  alRechazar,
}: {
  cert: Certificado;
  ocupado: boolean;
  alAceptar: () => void;
  alRechazar: () => void;
}) {
  return (
    <>
      <header className="mb-stack-lg">
        <h1 className="text-headline-md text-on-surface">Es la primera vez</h1>
        <p className="mt-1 text-body-md text-on-surface-variant">
          Esta tablet no conocía a <span className="font-mono">{cert.anfitrion}</span>. Compara esta
          huella con la de la etiqueta del equipo. Si son iguales, es la caseta correcta.
        </p>
      </header>

      <Huella valor={cert.huella} />

      <p className="mt-3 text-body-sm text-on-surface-variant">
        Después de aceptarla, esta tablet solo hablará con este equipo. No se te volverá a
        preguntar.
      </p>

      <div className="mt-stack-lg space-y-3">
        <Boton
          variante="primario"
          disabled={ocupado}
          icono={Iconos.correcto}
          className="w-full"
          onClick={alAceptar}
        >
          {ocupado ? 'Conectando…' : 'Coincide, es esta caseta'}
        </Boton>
        <Boton variante="texto" className="w-full" disabled={ocupado} onClick={alRechazar}>
          No coincide
        </Boton>
      </div>
    </>
  );
}

/**
 * Lo que responde no es lo que se esperaba. Es el caso que este esquema existe
 * para detectar, y hay dos maneras de llegar:
 *
 *   · `anclada` — esta tablet ya había emparejado con ese equipo y ahora
 *     responde otro certificado. La causa honesta existe: lo reinstalaron.
 *   · `etiqueta` — se acaba de escanear la etiqueta de un equipo y esa
 *     dirección responde con otra cosa. Aquí no hay causa honesta que valga
 *     reemparejar: o la dirección no es la de ese equipo, o alguien está en
 *     medio. Por eso no se ofrece seguir.
 */
function NoCoincide({
  cert,
  esperada,
  origen,
  alVolver,
  alReemparejar,
}: {
  cert: Certificado;
  esperada: string;
  origen: 'anclada' | 'etiqueta';
  alVolver: () => void;
  alReemparejar: () => void;
}) {
  const deEtiqueta = origen === 'etiqueta';
  return (
    <>
      <header className="mb-stack-lg">
        <h1 className="text-headline-md text-on-surface">
          {deEtiqueta ? 'No es el equipo de la etiqueta' : 'El servidor cambió de identidad'}
        </h1>
        <p className="mt-1 text-body-md text-on-surface-variant">
          <span className="font-mono">{cert.anfitrion}</span>{' '}
          {deEtiqueta
            ? 'responde con un certificado que no es el de la etiqueta que acabas de escanear.'
            : 'responde con un certificado distinto al que esta tablet emparejó.'}{' '}
          No se conectó nada.
        </p>
      </header>

      <p className="mb-2 text-label-bold text-on-surface-variant">
        {deEtiqueta ? 'Dice la etiqueta' : 'Se esperaba'}
      </p>
      <Huella valor={esperada} />

      <p className="mb-2 mt-4 text-label-bold text-on-surface-variant">Llegó</p>
      <Huella valor={cert.huella} tono="malo" />

      <ErrorCaja>
        {deEtiqueta
          ? 'O esa dirección es de otro equipo, o alguien en esta red se está haciendo pasar por la caseta. Revisa la dirección antes de seguir.'
          : 'Puede que alguien en esta red se esté haciendo pasar por la caseta. También pasa si al servidor lo reinstalaron. Averígualo antes de seguir.'}
      </ErrorCaja>

      <div className="mt-stack-lg space-y-3">
        <Boton variante="primario" className="w-full" icono={Iconos.cerrar} onClick={alVolver}>
          No conectar
        </Boton>
        {!deEtiqueta && (
          <Boton variante="texto" className="w-full" onClick={alReemparejar}>
            Al servidor lo reinstalaron: emparejar de nuevo
          </Boton>
        )}
      </div>
    </>
  );
}

/** La misma pantalla como ruta, para cambiar de servidor desde el login. */
export function ServidorPagina() {
  const navegar = useNavigate();

  // En el navegador esta ruta no significa nada; se llega por teclear la URL.
  if (!esAppNativa()) return <Navigate to="/login" replace />;

  const alLogin = () => navegar('/login', { replace: true });
  return <PantallaServidor alGuardar={alLogin} alCancelar={alLogin} />;
}
